// components/notifications/NotificationDropdown.tsx

'use client'

import Link from 'next/link'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Bell, Loader2 } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import { NotificationListItem } from './NotificationListItem'
import { cn } from '@/lib/utils'

const DROPDOWN_LIMIT = 5

export function NotificationDropdown() {
  const { unread, unreadCount, isLoading, error, markRead, markAllRead, markAllReadPending } =
    useNotifications()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
          className="relative inline-flex items-center justify-center rounded-md p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute top-1 right-1 inline-flex items-center justify-center',
                'min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none',
                'bg-red-500 text-white'
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b dark:border-zinc-800">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault()
                markAllRead()
              }}
              disabled={markAllReadPending}
              className="h-7 text-xs"
            >
              {markAllReadPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Mark all read'}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {error ? (
            <p className="px-3 py-6 text-center text-xs text-red-500">
              Failed to load. Try again later.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          ) : unread.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-zinc-500">No new notifications.</p>
          ) : (
            <ul className="divide-y dark:divide-zinc-800">
              {unread.slice(0, DROPDOWN_LIMIT).map((n) => (
                <li key={n.id}>
                  <NotificationListItem notification={n} onMarkRead={(id) => markRead([id])} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t dark:border-zinc-800 p-2">
          <Link
            href="/notifications"
            className="block w-full text-center text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline py-1"
          >
            See all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
